import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MonitorPlay, Camera, Cable, CheckCircle2, Award, Users, Wrench } from "lucide-react";
import { Button } from "../components/ui/button";
import { SERVICES, HERO_IMAGE, GALLERY } from "../lib/api";

const ICONS = { ShieldCheck, MonitorPlay, Camera, Cable };

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7 animate-fade-up">
            <h1 className="font-display font-black tracking-tight text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              Buildings that <span className="text-[#1E3A6B]">see, hear,</span> <span className="text-[#2D8557]">and respond.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Greenville Integrated Solutions designs and installs the electronic systems
              that make modern facilities secure, connected, and intelligent — access
              control, AV, surveillance, and certified structured cabling.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote">
                <Button data-testid="hero-quote-btn" className="rounded-none bg-[#1E3A6B] hover:bg-[#172F58] text-white h-12 px-6 text-base">
                  Request a Quote <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/assessment">
                <Button data-testid="hero-assessment-btn" variant="outline" className="rounded-none border-slate-300 hover:border-slate-900 h-12 px-6 text-base">
                  Free Site Assessment
                </Button>
              </Link>
            </div>
            <dl className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { k: "12+", v: "Years in business" },
                { k: "500+", v: "Projects delivered" },
                { k: "BICSI", v: "Certified technicians" },
              ].map((s) => (
                <div key={s.v} className="border-l-2 border-[#1E3A6B] pl-3">
                  <dt className="font-display font-bold text-2xl text-slate-900">{s.k}</dt>
                  <dd className="text-xs font-mono uppercase tracking-wider text-slate-500 mt-1">{s.v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] border border-slate-200 overflow-hidden bg-slate-100">
              <img src={HERO_IMAGE} alt="Modern conference room" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 via-black/20 to-transparent text-white">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] opacity-80">Project · Featured</div>
                <div className="font-display font-bold text-lg mt-1">Carolina Health · Boardroom AV</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden lg:block bg-white border border-slate-200 p-4 w-56 shadow-sm">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Uptime SLA</div>
              <div className="font-display font-black text-3xl text-slate-900 mt-1">99.98%</div>
              <div className="text-xs text-slate-600 mt-1">Across managed sites</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 lg:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#1E3A6B] mb-3">— Capabilities</div>
              <h2 className="font-display font-bold tracking-tight text-3xl lg:text-5xl text-slate-900 max-w-2xl">
                Four disciplines, one integrated system.
              </h2>
            </div>
            <p className="text-slate-600 max-w-md leading-relaxed">
              We design every install to interoperate — your camera, badge reader,
              boardroom display, and patch panel speaking the same language.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <article
                  key={s.slug}
                  data-testid={`service-card-${s.slug}`}
                  className="group bg-white border border-slate-200 hover:border-slate-900 transition-all hover:-translate-y-1 hover:shadow-lg flex flex-col"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <Icon size={22} className="text-[#1E3A6B]" />
                      <span className="text-[10px] font-mono text-slate-400">0{i + 1}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg text-slate-900">{s.name}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{s.short}</p>
                    <Link to="/services" className="mt-4 text-sm font-medium text-[#1E3A6B] inline-flex items-center gap-1 hover:gap-2 transition-all">
                      Learn more <ArrowRight size={14} />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#1E3A6B] mb-3">— Why Greenville</div>
            <h2 className="font-display font-bold tracking-tight text-3xl lg:text-5xl text-slate-900 leading-[1.05]">
              Engineered installations.<br/>Documented every cable.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              We're a small, senior team — every project is touched by a lead engineer.
              We hand off labeled racks, patched panels, and clean as-builts. Your IT team
              will thank us.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { I: Award, t: "Manufacturer-certified", d: "Avigilon, Crestron, Genetec, LenelS2 partners." },
              { I: Wrench, t: "Single accountable team", d: "Design, install, and service — no subbing it out." },
              { I: Users, t: "Commercial focus", d: "Healthcare, education, finance, & multi-site retail." },
              { I: CheckCircle2, t: "1-year warranty", d: "On installation labor, with optional managed service." },
            ].map((b) => (
              <div key={b.t} className="border border-slate-200 p-6 bg-[#FAFAFA]">
                <b.I className="text-[#1E3A6B]" size={22} />
                <h3 className="font-display font-bold mt-4 text-slate-900">{b.t}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 lg:py-28">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#1E3A6B] mb-3">— Selected Work</div>
          <h2 className="font-display font-bold tracking-tight text-3xl lg:text-5xl text-slate-900 mb-10 max-w-2xl">
            From data centers to boardrooms.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-7 aspect-[4/3] border border-slate-200 overflow-hidden bg-slate-100">
              <img src={GALLERY[0]} alt="Data center patch panel" className="w-full h-full object-cover" />
            </div>
            <div className="md:col-span-5 grid grid-rows-2 gap-6">
              <div className="border border-slate-200 overflow-hidden bg-slate-100">
                <img src={GALLERY[1]} alt="Server room" className="w-full h-full object-cover" />
              </div>
              <div className="border border-slate-200 overflow-hidden bg-slate-100">
                <img src={GALLERY[2]} alt="Conference room" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 lg:py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display font-bold tracking-tight text-3xl lg:text-5xl">
              Ready to scope your project?
            </h2>
            <p className="mt-4 text-slate-300 max-w-xl leading-relaxed">
              Send us a quick brief or book a free on-site assessment. A senior engineer
              will respond within one business day.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to="/quote">
              <Button data-testid="cta-quote-btn" className="w-full rounded-none bg-[#2D8557] hover:bg-[#236944] h-12 text-base">
                Request a Quote
              </Button>
            </Link>
            <Link to="/assessment">
              <Button data-testid="cta-assessment-btn" variant="outline" className="w-full rounded-none border-slate-700 bg-transparent text-white hover:bg-white hover:text-slate-900 h-12 text-base">
                Schedule Site Assessment
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
